import { FC, useMemo } from "react"
import { ControlProps, JsonSchema } from "@jsonforms/core"
import { withJsonFormsControlProps } from "@jsonforms/react"
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
} from "@mui/material"
import InputSummary from "../NewForm/InputSummary"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import UTooltip from "@/containers/LandingLayout/PatientInfoHeader/UTootip"

type ArchData = {
    active_retainer?: string
}

type ArchSchema = JsonSchema & {
    label?: string
    properties?: {
        active_retainer?: JsonSchema & {
            label?: string
            default?: string
        }
    }
}

const formatArchLabel = (archKey: string) =>
    archKey.charAt(0).toUpperCase() + archKey.slice(1)

const ArchElement: FC<{
    archKey: string
    archSchema: ArchSchema
    archData: ArchData
    onChange: (archKey: string, checked: boolean) => void
}> = ({ archKey, archSchema, archData, onChange }) => {
    const archLabel = archSchema.label || formatArchLabel(archKey)
    const checkboxLabel =
        archSchema.properties?.active_retainer?.label || "Active Retainer"

    return (
        <>
            <Box
                sx={{
                    flexDirection: "row",
                    display: "flex",
                    mb: 0,
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked
                            disabled
                            sx={{
                                "&.Mui-checked": {
                                    color: "grey",
                                },
                            }}
                            name={archKey}
                        />
                    }
                    label={archLabel}
                />
            </Box>
            <Box
                sx={{
                    flexDirection: "row",
                    display: "flex",
                    mx: 4,
                    mt: 0,
                    mb: 1,
                    alignItems: "center",
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(archData?.active_retainer?.trim())}
                            onChange={(_, checked) => onChange(archKey, checked)}
                            name={`${archKey}-active-retainer`}
                        />
                    }
                    label={checkboxLabel}
                />
                <UTooltip
                    titleContent={
                        <Box sx={{ p: 1 }}>
                            Minor reset limited to one stage; retainer only
                        </Box>
                    }
                    placement="right"
                >
                    <InfoOutlinedIcon
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                            fontSize: 18,
                            color: "rgba(0,0,0,0.6)",
                            cursor: "pointer",
                        }}
                    />
                </UTooltip>
            </Box>
        </>
    )
}

const UArchesRetainer: FC<ControlProps> = (props) => {
    const { handleChange, path, data: idata, label, schema: ischema } = props
    const schema = ischema as JsonSchema & {
        properties?: Record<string, ArchSchema>
        summary?: boolean
    }
    const archEntries = useMemo(
        () => Object.entries(schema?.properties || {}),
        [schema],
    )
    const data = idata || {}

    const updateArchSelection = (archKey: string, checked: boolean) => {
        const activeRetainerSchema =
            schema?.properties?.[archKey]?.properties?.active_retainer
        const nextValue =
            checked
                ? activeRetainerSchema?.label ||
                activeRetainerSchema?.default ||
                "Active Retainer"
                : ""

        handleChange(path, {
            ...data,
            [archKey]: {
                ...(data?.[archKey] || {}),
                isChecked: true,
                active_retainer: nextValue,
            },
        })
    }

    const generateSummary = (data: any): string => {
        if (!data) return "No arches selected."
        const formatArch = (label: string, archData: any): string | null => {
            if (!archData) {
                return null
            }
            const headerValue = archData.active_retainer?.trim()
            if (!headerValue) {
                return label
            }
            return `${label} : ${headerValue}`
        }
        const summary = [
            formatArch("Upper", data.upper),
            formatArch("Lower", data.lower),
        ].filter(Boolean)

        return summary.length > 0 ? summary.join("\n\n") : "No arches selected."
    }


    return (
        <>
            {schema?.summary ? (
                <InputSummary label={label} inputVal={generateSummary(data)} />
            ) : (
                <FormControl
                    component="fieldset"
                    variant="standard"
                    sx={{ my: 1 }}
                >
                    <FormLabel
                        sx={{
                            my: 2,
                            color: "rgba(0, 0, 0, 0.60)",
                            "&.Mui-focused": {
                                color: "rgba(0, 0, 0, 0.60)",
                            },
                        }}
                    >
                        {label}
                    </FormLabel>
                    {schema &&
                        archEntries.map(([archKey, archSchema]) => (
                            <Box key={archKey}>
                                <ArchElement
                                    archKey={archKey}
                                    archSchema={archSchema}
                                    archData={data?.[archKey] || {}}
                                    onChange={updateArchSelection}
                                />
                            </Box>
                        ))}

                </FormControl>
            )}
        </>
    )
}

export default withJsonFormsControlProps(UArchesRetainer)
